import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, ExternalLink, Loader2, Upload, Plus, X } from "lucide-react";
import MarkdownEditor from "../../components/MarkdownEditor";
import { adminApi } from "../../lib/adminApi";

const EMPTY = {
  title: "", slug: "", excerpt: "",
  cover_image_url: "", cover_image_alt: "",
  status: "draft",
  client_name: "", client_industry: "", client_location: "",
  service_type: "",
  challenge: "", solution: "", results: "",
  metrics: [],
  testimonial_quote: "", testimonial_author: "",
  seo_title: "", seo_description: "",
  tags: [],
};

export default function AdminCaseEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const [cs, setCs] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const res = await adminApi.getCase(id);
      if (res.ok) setCs({ ...EMPTY, ...res.data });
      else setError(res.error);
      setLoading(false);
    })();
  }, [id, isNew]);

  const update = (k, v) => setCs((c) => ({ ...c, [k]: v }));

  const save = async (publish = false) => {
    setSaving(true); setError(null); setStatusMsg(null);
    const body = { ...cs, status: publish ? "published" : cs.status };
    const res = isNew ? await adminApi.createCase(body) : await adminApi.updateCase(id, body);
    setSaving(false);
    if (!res.ok) { setError(res.error); return; }
    setStatusMsg(publish ? "Published." : "Saved.");
    if (isNew) navigate(`/admin/case-studies/${res.data.id}`, { replace: true });
    else setCs({ ...EMPTY, ...res.data });
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const res = await adminApi.uploadImage(file);
    setUploading(false);
    if (res.ok) update("cover_image_url", res.data.url);
    else alert(res.error);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!cs.tags.includes(t)) update("tags", [...cs.tags, t]);
    setTagInput("");
  };
  const setMetric = (idx, key, val) => {
    const next = [...(cs.metrics || [])];
    next[idx] = { ...(next[idx] || { label: "", value: "" }), [key]: val };
    update("metrics", next);
  };
  const addMetric = () => update("metrics", [...(cs.metrics || []), { label: "", value: "" }]);
  const rmMetric = (idx) => update("metrics", (cs.metrics || []).filter((_, i) => i !== idx));

  if (loading) return <p className="p-6 text-sm text-ridge-muted">Loading…</p>;

  return (
    <div data-testid="admin-case-edit">
      <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <button type="button" onClick={() => navigate("/admin/case-studies")} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-muted hover:text-ridge-charcoal">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">
            {isNew ? "New Case Study" : "Edit Case Study"}
          </h1>
          {cs.slug && !isNew ? (
            <p className="mt-1 text-xs text-ridge-muted">
              Slug: <code className="rounded bg-ridge-stone px-1.5 py-0.5">{cs.slug}</code>
              {cs.status === "published" ? <a href={`/case-studies/${cs.slug}`} target="_blank" rel="noreferrer" className="ml-2 text-ridge-amber">View live <ExternalLink className="ml-1 inline h-3 w-3" /></a> : null}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => save(false)} disabled={saving} className="btn-secondary px-5 py-2 text-xs" data-testid="admin-case-save-draft">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
          <button type="button" onClick={() => save(true)} disabled={saving} className="btn-accent px-5 py-2 text-xs" data-testid="admin-case-publish">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Publish
          </button>
        </div>
      </header>

      {error ? <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p> : null}
      {statusMsg ? <p className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{statusMsg}</p> : null}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-8">
          <Field label="Title" required>
            <input type="text" value={cs.title} onChange={(e) => update("title", e.target.value)} className={inputCls} data-testid="admin-case-title" />
          </Field>
          <Field label="Slug" hint="Auto-generated if empty.">
            <input type="text" value={cs.slug || ""} onChange={(e) => update("slug", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Excerpt" hint="One-sentence summary for listings.">
            <textarea value={cs.excerpt || ""} onChange={(e) => update("excerpt", e.target.value)} rows={2} maxLength={400} className={inputCls} />
          </Field>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Client name">
              <input type="text" value={cs.client_name || ""} onChange={(e) => update("client_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Client industry">
              <input type="text" value={cs.client_industry || ""} onChange={(e) => update("client_industry", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Client location">
              <input type="text" value={cs.client_location || ""} onChange={(e) => update("client_location", e.target.value)} placeholder="e.g., Mississauga, ON" className={inputCls} />
            </Field>
            <Field label="Service type">
              <input type="text" value={cs.service_type || ""} onChange={(e) => update("service_type", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label="The Challenge (markdown)">
            <MarkdownEditor value={cs.challenge} onChange={(v) => update("challenge", v)} rows={10} testId="admin-case-challenge" />
          </Field>
          <Field label="Our Solution (markdown)">
            <MarkdownEditor value={cs.solution} onChange={(v) => update("solution", v)} rows={10} testId="admin-case-solution" />
          </Field>
          <Field label="Results (markdown)">
            <MarkdownEditor value={cs.results} onChange={(v) => update("results", v)} rows={10} testId="admin-case-results" />
          </Field>

          {/* Metrics */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">Metrics</span>
              <button type="button" onClick={addMetric} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-amber hover:text-ridge-amber-dark">
                <Plus className="h-3.5 w-3.5" /> Add metric
              </button>
            </div>
            <div className="space-y-2">
              {(cs.metrics || []).map((m, idx) => (
                <div key={idx} className="flex flex-col gap-2 rounded-md border border-ridge-border bg-white p-3 sm:flex-row">
                  <input type="text" value={m.label || ""} onChange={(e) => setMetric(idx, "label", e.target.value)} placeholder="Label (e.g., On-time delivery)" className={inputCls + " sm:flex-1"} />
                  <input type="text" value={m.value || ""} onChange={(e) => setMetric(idx, "value", e.target.value)} placeholder="Value (e.g., 100%)" className={inputCls + " sm:w-40"} />
                  <button type="button" onClick={() => rmMetric(idx)} className="self-center text-ridge-muted hover:text-red-600" aria-label="Remove">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {(cs.metrics || []).length === 0 ? <p className="text-xs text-ridge-muted">No metrics yet.</p> : null}
            </div>
          </div>

          <Field label="Testimonial quote">
            <textarea value={cs.testimonial_quote || ""} onChange={(e) => update("testimonial_quote", e.target.value)} rows={3} maxLength={1000} className={inputCls} />
          </Field>
          <Field label="Testimonial author" hint="Name + title (e.g., Jane Doe, Director of Ops at Acme Labs)">
            <input type="text" value={cs.testimonial_author || ""} onChange={(e) => update("testimonial_author", e.target.value)} className={inputCls} />
          </Field>
        </div>

        <aside className="space-y-5 lg:col-span-4">
          <Field label="Status">
            <select value={cs.status} onChange={(e) => update("status", e.target.value)} className={inputCls}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </Field>
          <Field label="Cover image URL">
            <input type="url" value={cs.cover_image_url || ""} onChange={(e) => update("cover_image_url", e.target.value)} className={inputCls} />
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-amber hover:text-ridge-amber-dark">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : "Upload image"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="sr-only" onChange={onCoverUpload} />
            </label>
            {cs.cover_image_url ? <img src={cs.cover_image_url} alt="" className="mt-3 max-h-40 w-full rounded-md border border-ridge-border object-cover" /> : null}
          </Field>
          <Field label="Cover image alt">
            <input type="text" value={cs.cover_image_alt || ""} onChange={(e) => update("cover_image_alt", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Tags">
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ridge-border bg-white p-2">
              {(cs.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded bg-ridge-stone px-2 py-0.5 text-xs font-semibold text-ridge-charcoal">
                  {t}
                  <button type="button" onClick={() => update("tags", cs.tags.filter((x) => x !== t))} className="text-ridge-muted hover:text-red-600">×</button>
                </span>
              ))}
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }} placeholder="Type & press Enter" className="flex-1 min-w-[120px] bg-transparent px-1 py-1 text-sm focus:outline-none" />
            </div>
          </Field>
          <Field label="SEO title"><input type="text" value={cs.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} className={inputCls} /></Field>
          <Field label="SEO description"><textarea value={cs.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} rows={3} maxLength={400} className={inputCls} /></Field>
        </aside>
      </div>
    </div>
  );
}

const inputCls = "block w-full rounded-md border border-ridge-border bg-white px-3 py-2 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15";
function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">{label}{required ? <span className="ml-1 text-ridge-amber">*</span> : null}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-ridge-muted">{hint}</p> : null}
    </div>
  );
}
